import React from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const capabilities = [{
  title: "Precision 45-Degree Mitred Edges",
  description: "We specialize in the technical art of mitring tile edges to a perfect 45-degree angle. This eliminates the need for plastic or metal trims, creating a seamless, solid-stone appearance that defines true luxury craftsmanship.",
  features: ["Seamless corner transitions", "No unsightly plastic trims", "High-end monolithic look", "Expert cutting precision"],
  image: "https://simprotile.com/wp-content/uploads/2026/03/20250403_181635-scaled.jpg?v=rhmafmxwbwp",
  alt: "Close up of precision 45-degree mitred tile corner"
}, {
  title: "Epoxy Grout Finishing",
  description: "Unlike standard cement-based grout, our epoxy grout solutions offer superior durability, stain resistance, and waterproofing. It is the gold standard for hygiene and longevity in wet areas.",
  features: ["100% waterproof and stain-proof", "Resistant to cracking and shrinking", "Ideal for walk-in showers", "Available in color-matched shades"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1571404/img/epoxy_grout_application.png",
  alt: "Professional application of epoxy grout in a modern bathroom"
}, {
  title: "Wet Rooms & Walk-In Showers",
  description: "Transform your bathroom into a spacious, open-plan wet room. We install complete waterproof tanking systems and pre-formed shower trays to ensure a leak-free, level-access showering experience.",
  features: ["Complete waterproof tanking", "Level-access floor systems", "Ditra matting installation", "High-flow drainage solutions"],
  image: "https://simprotile.com/wp-content/uploads/2026/03/20231004_195619-scaled.jpg?v=y76j7jp2ro",
  alt: "Luxury wet room with walk-in shower and rainfall head"
}, {
  title: "Large Format Porcelain Installation",
  description: "Handling large format tiles requires specialized tools and expertise. We ensure perfectly flat surfaces with self-leveling compounds and precision clip systems to eliminate lippage.",
  features: ["Lippage-free installation", "Self-leveling floor preparation", "Handling tiles up to 2.4m+", "Minimal grout line visibility"],
  image: "https://simprotile.com/wp-content/uploads/2026/03/20231016_202337-scaled.jpg?v=k6d88wm2for",
  alt: "Spacious living area with large format porcelain floor tiles"
}];
export default function OurFullCapability() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="our-full-capability" className="py-24 overflow-hidden text-left !bg-[hsl(210,17%,98%)] !text-[hsl(0,0%,0%)]" ref={ref}>
      <style>{`
        :root {
          --background: hsl(0 0% 100%);
          --foreground: hsl(0 0% 95%);
          --card-foreground: hsl(0 0% 95%);
          --popover-foreground: hsl(0 0% 95%);
          --primary-foreground: hsl(0 0% 95%);
          --secondary-foreground: hsl(0 0% 95%);
          --muted-foreground: hsl(0 0% 95%);
          --sidebar-foreground: hsl(0 0% 95%);
        }
        .dark {
          --foreground: hsl(0 0% 95%);
          --card-foreground: hsl(0 0% 95%);
          --popover-foreground: hsl(0 0% 95%);
          --primary-foreground: hsl(0 0% 95%);
          --secondary-foreground: hsl(0 0% 95%);
          --muted-foreground: hsl(0 0% 95%);
          --sidebar-foreground: hsl(0 0% 95%);
        }
      `}</style>
      <div className="container ml-0 mr-auto px-4 md:px-6">
        <div className="max-w-3xl ml-0 text-left mb-20">
          <h2 className="text-3xl font-bold mb-6 tracking-tight transition-all duration-700 transform opacity-100 translate-y-0 !text-[hsl(0,0%,0%)] md:text-4xl">
            Our Full Capability
          </h2>
          <p className="text-lg transition-all duration-700 delay-100 transform opacity-100 translate-y-0 !text-[hsl(0,0%,0%)]">
            From technical waterproofing to the finest aesthetic details, we
            deliver a complete range of premium tiling services across London.
          </p>
        </div>

        <div className="space-y-24">
          {capabilities.map((item, index) => {
          const isEven = index % 2 === 0;
          return <div key={index} data-index={index} className={cn("flex flex-col lg:flex-row justify-start items-start gap-12 lg:gap-20 transition-all duration-1000 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16")} style={{
            transitionDelay: `${index * 150}ms`
          }}>
                <div className={cn("w-full lg:w-1/2 relative group", isEven ? "lg:order-1" : "lg:order-2")}>
                  <div className="relative overflow-hidden rounded-xl shadow-xl aspect-[4/3] bg-muted">
                    <img src={item.image} alt={item.alt} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </div>

                <div className={cn("w-full lg:w-1/2 space-y-6", isEven ? "lg:order-2" : "lg:order-1")}>
                  <h3 className="text-2xl font-bold !text-[hsl(222,47%,11%)] md:text-3xl">
                    {item.title}
                  </h3>
                  <p className="text-lg leading-relaxed !text-[hsl(0,0%,0%)]">
                    {item.description}
                  </p>

                  <ul className="grid grid-cols-1 sm:grid-cols-2 justify-start gap-y-3 gap-x-4 pt-2">
                    {item.features.map((feature, fIndex) => <li key={fIndex} data-index={fIndex} className="flex justify-start items-start gap-3">
                        <span className="mt-1 flex-shrink-0 bg-primary/10 p-1 rounded-full text-primary">
                          <Check className="w-4 h-4" strokeWidth={3} />
                        </span>
                        <span className="font-medium !text-[hsl(0,0%,0%)]">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <div className="pt-4">
                    <Button asChild size="lg">
                      <Link to={index === 0 ? "https://simprotile.com/get-a-quote-2/" : "#"}>
                        Get a Free Quote
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>;
        })}
        </div>

        <div className="mt-24 text-left">
          <Button asChild size="lg" className="text-lg px-8 py-6 shadow-lg">
            <Link to="#">View All Services</Link>
          </Button>
        </div>
      </div>
    </section>;
}